import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { QcField } from '@nucleus-commerce/api-client/admin';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

/** yes_no a boolean, number a number, choice and text a string, file a media id. */
export type QcAnswer = boolean | number | string | null;

const OTHER = '__other__';

export interface QcFieldInputProps {
  field: Pick<QcField, 'key' | 'label' | 'type' | 'options' | 'allowOther'>;
  value: QcAnswer;
  onChange: (value: QcAnswer) => void;
  invalid?: boolean;
  disabled?: boolean;
  /** Ids of the hint and error under the field. */
  describedBy?: string;
  /** What a file question renders: an uploader on the order, a placeholder in the preview. */
  fileSlot?: ReactNode;
}

/**
 * One QC answer control, shared by the checklist builder's preview and the order's QC form so
 * both show the form exactly as staff fill it in. The caller renders the question label.
 */
export function QcFieldInput({
  field,
  value,
  onChange,
  invalid,
  disabled,
  describedBy,
  fileSlot,
}: QcFieldInputProps) {
  const { t } = useTranslation('workflows');
  const id = useId();
  const aria = {
    'aria-invalid': invalid ? true : undefined,
    'aria-describedby': describedBy,
  };
  const inOptions = typeof value === 'string' && field.options.includes(value);
  const [otherChosen, setOtherChosen] = useState(
    () => field.allowOther && typeof value === 'string' && value !== '' && !inOptions,
  );

  if (field.type === 'yes_no') {
    return (
      <RadioGroup
        aria-label={field.label}
        className="flex gap-4"
        value={value === true ? 'yes' : value === false ? 'no' : ''}
        onValueChange={(next) => onChange(next === 'yes')}
        disabled={disabled}
        {...aria}
      >
        {(['yes', 'no'] as const).map((option) => (
          <div key={option} className="flex items-center gap-2">
            <RadioGroupItem id={`${id}-${option}`} value={option} />
            <Label htmlFor={`${id}-${option}`} className="font-normal">
              {t(`qc.answer.${option}`)}
            </Label>
          </div>
        ))}
      </RadioGroup>
    );
  }

  if (field.type === 'choice') {
    const selected = otherChosen ? OTHER : typeof value === 'string' && inOptions ? value : '';
    return (
      <div className="flex flex-col gap-2">
        <RadioGroup
          aria-label={field.label}
          className="flex flex-wrap gap-x-4 gap-y-2"
          value={selected}
          disabled={disabled}
          onValueChange={(next) => {
            if (next === OTHER) {
              setOtherChosen(true);
              onChange('');
            } else {
              setOtherChosen(false);
              onChange(next);
            }
          }}
          {...aria}
        >
          {field.options.map((option, index) => (
            <div key={option} className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-${index}`} value={option} />
              <Label htmlFor={`${id}-${index}`} className="font-normal">
                {option}
              </Label>
            </div>
          ))}
          {field.allowOther ? (
            <div className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-other`} value={OTHER} />
              <Label htmlFor={`${id}-other`} className="font-normal">
                {t('qc.answer.other')}
              </Label>
            </div>
          ) : null}
        </RadioGroup>
        {otherChosen ? (
          <Input
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('qc.answer.otherPlaceholder')}
            aria-label={`${field.label}: ${t('qc.answer.other')}`}
            maxLength={2000}
            disabled={disabled}
            className="sm:max-w-sm"
            {...aria}
          />
        ) : null}
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <Input
        type="number"
        inputMode="decimal"
        aria-label={field.label}
        value={typeof value === 'number' ? String(value) : ''}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' || Number.isNaN(Number(raw)) ? null : Number(raw));
        }}
        disabled={disabled}
        className="sm:max-w-48"
        {...aria}
      />
    );
  }

  if (field.type === 'file') {
    return (
      fileSlot ?? (
        <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
          {t('qc.answer.filePlaceholder')}
        </p>
      )
    );
  }

  return (
    <Input
      aria-label={field.label}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      maxLength={2000}
      disabled={disabled}
      {...aria}
    />
  );
}
